// Buying a sound: sale terms and the sheet's facts, ported from the app
// (flask pricing.sale_terms; lib/screens/sounds/sound_detail.dart,
// money_field.dart; lib/models/sound.dart displayKey) so the web sheet says
// what the app says.
import { keyName } from "./musicKey.js";

const OPEN = new Set([
  "CC0", "CC-BY", "CC-BY-NC", "CC-BY-SA", "CC-BY-NC-SA", "CC-BY-ND", "CC-BY-NC-ND",
]);

// pricing.sale_terms: "open" (Creative Commons), "custom" (contact the
// creator), "not_for_sale" (rights reserved with no price, decision 15:
// never free) or "for_sale" with the minimum, the suggested price and the
// least a checkout takes.
export function saleTerms(md) {
  const type = (md && md.license_type) || "CC0";
  const out = { state: "custom", min: null, suggested: null, checkoutMin: null };
  if (OPEN.has(type)) return { ...out, state: "open" };
  if (type !== "rights-reserved") return out;
  const min = Math.max(Number(md.min_price_usd) || 0, 0);
  let suggested = md.suggested_price_usd == null ? null : Number(md.suggested_price_usd);
  if (suggested != null && suggested <= 0) suggested = null;
  let floor;
  if (md.for_sale === true) floor = min;
  else if (min > 0 || suggested != null) floor = Math.max(min, 0.01);
  else return { ...out, state: "not_for_sale" };
  if (suggested != null && suggested < floor) suggested = floor;
  return { state: "for_sale", min, suggested, checkoutMin: floor };
}

// The least a checkout takes for a row that names its terms (a fork
// quote's missing loop, /loops/<hash>/access): its checkout_min_usd when
// the server sent one, else what sale_terms makes of the minimum and the
// suggested price (a suggested price alone still charges a cent).
export function checkoutMinOf(row) {
  if (!row) return 0;
  if (row.checkout_min_usd != null && Number.isFinite(Number(row.checkout_min_usd))) {
    return Math.max(Number(row.checkout_min_usd), 0);
  }
  const t = saleTerms({ license_type: "rights-reserved", ...row });
  if (t.checkoutMin != null) return t.checkoutMin;
  return Math.max(Number(row.min_price_usd) || 0, 0);
}

// The buy sheet's price line and button (S7). `live` is saleTerms of the
// sound's /api/loop answer (or a pack's terms), null until it lands;
// `failed` when it never will. Until the live terms are known the floor is
// at least a cent and the note says the price is being checked: a priced
// sound is never offered as free, or for $0, on the props alone.
//   { floor, pricing, valid, note, cta }
export function buyPrice({ minPrice = 0, live = null, failed = false, amount = null, signedIn = true } = {}) {
  const known = !!live;
  const pricing = !known && !failed;
  const floor = known ? Math.max(Number(minPrice) || 0, live.checkoutMin || 0) : Math.max(Number(minPrice) || 0, 0.01);
  const valid = !pricing && amount !== null && amount >= floor;
  let note;
  if (pricing) note = "Checking the price…";
  else if (floor <= 0) note = "Free, or pay what you want.";
  else note = `Name your price · minimum ${usd(floor)}`;
  let cta;
  if (!signedIn) cta = "Log in to buy";
  else if (!valid) cta = "Buy";
  else cta = amount <= 0 ? "Get it free" : `Buy · ${usd(amount)}`;
  return { floor, pricing, valid, note, cta };
}

// Where a buy sheet starts (the app's SoundPrice.startUsd): the suggested
// price, never under the checkout minimum.
export function startUsd({ checkoutMin, suggested } = {}) {
  const floor = Number(checkoutMin) || 0;
  return suggested == null ? floor : Math.max(Number(suggested), floor);
}

// What a sound's price chip says, on a session row and a pack row alike
// (the app's sound_row: `s.chipPriceUsd ?? s.price?.startUsd`, where
// chipPriceUsd is the checkout minimum). The chip names what it costs at
// least; the sheet it opens starts at startUsd, as in the app.
export function chipPriceUsd(price) {
  if (!price) return null;
  if (price.checkoutMin != null) return Number(price.checkoutMin);
  return price.suggested == null ? null : startUsd(price);
}

// What a session row offers the viewer for a loop (decision 25). `info` is
// the loop's sale terms and access: signed in, GET /loops/<hash>/access;
// signed out, the loop's entry in the session's /media/session-urls answer,
// which carries the same fields (accessible, license_state,
// checkout_min_usd, min_price_usd, suggested_price_usd).
//   needsPurchase: the full-quality WAV is not yours to take
//   saleState:     "for_sale" | "not_for_sale" | "custom" | null
//   canDownload:   offer "Download loop (.wav)"
// Unknown access (the answer failed or hasn't come): signed in, the
// download stays offered and the server is the gate, as before; signed
// out there is no download token, so nothing is offered that can't work.
export function loopOffer(info, { signedIn = false } = {}) {
  if (!info) return { needsPurchase: false, saleState: null, canDownload: signedIn };
  if (info.accessible !== false) return { needsPurchase: false, saleState: null, canDownload: true };
  return {
    needsPurchase: true,
    saleState: info.license_state || saleTerms(info).state,
    canDownload: false,
  };
}

// "$2.99"
export function usd(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

// "0:04.0" from seconds.
export function formatLength(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

// "874 KB", "1.2 MB" (decimal units, as Finder shows file sizes).
export function formatBytes(bytes) {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
}

// The one key spelling (src/kit/musicKey.js): "F♯m", "B♭".
export { keyName };

// "F♯m · 120" (the sheet's subtitle tail).
export function keyBpm(md) {
  if (!md) return "";
  const parts = [];
  const key = keyName(md.key_pitch_class, md.key_is_minor);
  if (key) parts.push(key);
  if (md.bpm) parts.push(String(Math.round(md.bpm)));
  return parts.join(" · ");
}

// The file facts under the license, as the app's _facts() prints them:
// "0:04.0", "48 kHz stereo WAV", "874 KB", "12 downloads". Length, rate and
// size are left out when the data doesn't have them; downloads always show.
export function soundFacts(md) {
  if (!md) return [];
  const out = [];
  const length = Number(md.duration);
  if (length > 0) out.push(formatLength(length));
  const rate = Number(md.sample_rate);
  if (rate > 0) {
    const khz = (rate / 1000).toFixed(rate % 1000 === 0 ? 0 : 1);
    const ch = { 1: "mono", 2: "stereo" }[md.channels] || (md.channels > 2 ? `${md.channels}-channel` : "");
    out.push(`${khz} kHz${ch ? " " + ch : ""} WAV`);
  }
  if (Number(md.size) > 0) out.push(formatBytes(Number(md.size)));
  const n = Math.max(Number(md.downloads) || 0, 0);
  out.push(`${n} ${n === 1 ? "download" : "downloads"}`);
  return out;
}

// "Or get the whole pack": "Night drive · 5 sounds · $8.00" (the app's
// _packCard line; a pack with no price is free).
export function packLine({ title, total, price }) {
  return [
    title,
    total > 0 ? `${total} ${total === 1 ? "sound" : "sounds"}` : null,
    price == null || Number(price) <= 0 ? "Free" : usd(price),
  ]
    .filter(Boolean)
    .join(" · ");
}

// What a refused checkout (POST /api/purchases) means, in plain words. The
// server answers 409 for several reasons, so read its error code and
// message, never the status alone. `err` is { status, error, message, min }
// (see the mixin's startPurchase), or the bare status; `floor` is the
// sheet's minimum.
export function purchaseErrorText(err, floor = 0, { pack = false } = {}) {
  const e = typeof err === "number" ? { status: err } : err || {};
  const msg = String(e.message || "");
  const thing = pack ? "pack" : "sound";
  const owned = pack ? "You already own this pack." : "You already own a license for this loop.";
  if (/already own/i.test(msg) && (e.status === 409 || e.status === 400)) return owned;
  if (e.status === 409) {
    if (e.error === "not_for_sale") return `This ${thing} isn't for sale right now.`;
    if (/gone through/i.test(msg))
      return `Your earlier checkout went through. The ${thing} is in My audio, Purchased.`;
    if (/being processed/i.test(msg))
      return "Your earlier checkout is still being processed. Try again in a few minutes.";
    if (/still open/i.test(msg))
      return `An earlier checkout for this ${thing} is still open. Finish or cancel it at PayPal, or try again in a few minutes.`;
    return "Couldn't start checkout. Try again.";
  }
  if (e.status === 412) return "The seller hasn't linked a payout method yet.";
  if (e.status === 400 && (e.error === "below_minimum" || e.min != null || !msg || /amount|minimum|at least/i.test(msg))) {
    const min = e.min != null ? Number(e.min) : floor;
    return `Enter at least ${usd(min)}.`;
  }
  return "Couldn't start checkout. Try again.";
}
